import cx from "classnames";
import { useCallback } from "react";

import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import BaseActionButton from "ds/components/BaseAction/Button";
import { ChevronRight } from "components/icons/generated";
import Icon from "ds/components/Icon";
import useMainNavigationStore from "components/MainNavigation/stores/useMainNavigationStore";
import useMainNavigationActiveStates from "components/MainNavigation/hooks/useActiveStates";
import useMainNavigationFadeTransition from "components/MainNavigation/hooks/useFadeTransition";
import { selectNavigationShowsExpandedContent } from "components/MainNavigation/selectors/navigationState";
import useUserDropdownGroup from "components/MainNavigation/config/useUserDropdownGroup";
import { MainNavigationSecondaryMenuItemKeys } from "components/MainNavigation/config/menuItemsSecondary";
import useMainNavigationDropdownManager from "components/MainNavigation/hooks/useDropdownManager";
import MainNavigationGroupItem, {
  DropdownTriggerRenderFn,
} from "components/MainNavigation/components/elements/GroupItem";

import UserCard from "./UserCard";
import MainNavigationAccountDetailsMenuItem from "./AccountDetailsMenuItem";
import styles from "./styles.module.css";
import { MainNavigationDropdownMenuItemsCustomRendererProps } from "../../../atoms/DropdownMenu/Items";
import MainNavigationLoginList from "../LoginList";

const MainNavigationUserDropdown = () => {
  const { accountName, viewer } = useTypedContext(AccountContext);

  const { activeGroupId } = useMainNavigationActiveStates();
  const showsExpandedContent = useMainNavigationStore(selectNavigationShowsExpandedContent);
  const group = useUserDropdownGroup();

  const { isOpen } = useMainNavigationDropdownManager(group.id);

  const isGroupActive = activeGroupId === group.id;

  const { transitionClasses } = useMainNavigationFadeTransition(showsExpandedContent);

  const triggerComponent: DropdownTriggerRenderFn = useCallback(
    ({ onPress }) => (
      <BaseActionButton
        aria-label="User menu"
        justify="between"
        className={cx(
          styles.userDropdownControl,
          showsExpandedContent && styles.isSidebarExpanded,
          isGroupActive && styles.hasActiveChild,
          isOpen && styles.isFocused
        )}
        onPress={onPress}
        fullWidth
      >
        <UserCard
          transitionClasses={transitionClasses}
          accountName={accountName}
          viewer={viewer}
          isSidebarExpanded={showsExpandedContent}
        />
        <Icon src={ChevronRight} className={transitionClasses} noShrink />
      </BaseActionButton>
    ),
    [accountName, isGroupActive, isOpen, showsExpandedContent, transitionClasses, viewer]
  );

  const customRenderers = {
    [MainNavigationSecondaryMenuItemKeys.ChangeAccount]: ({
      key,
      closeDropdown,
    }: MainNavigationDropdownMenuItemsCustomRendererProps) => (
      <MainNavigationLoginList
        key={key}
        accountName={accountName}
        onCloseDropdown={closeDropdown}
      />
    ),
    [MainNavigationSecondaryMenuItemKeys.AccountDetails]: ({
      key,
      item,
      closeDropdown,
    }: MainNavigationDropdownMenuItemsCustomRendererProps) => (
      <MainNavigationAccountDetailsMenuItem key={key} item={item} onCloseDropdown={closeDropdown} />
    ),
  };

  return (
    <MainNavigationGroupItem
      group={group}
      dropdownOnly
      dropdownTriggerComponent={triggerComponent}
      dropdownMenuItemsCustomRenderers={customRenderers}
      dropdownClassName={styles.dropdown}
    />
  );
};

export default MainNavigationUserDropdown;
